/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import dataProvider from '../../authentication/dataProvider';
import { UserRecord } from '../../types/users/users';
import { User, UserStatus, UserFormData } from '../../types/user';
import { UsersTable } from './UsersTable';
import { UsersFilters } from './UsersFilters';
import { AddUserModal } from './AddUserModal';
import { toast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import ApproveViewsButton from './approve.view.buttons';

const Users = () => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [filterRoleId, setFilterRoleId] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<UserStatus[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(handle);
  }, [search]);

  // Any filter change should reset to the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, filterRoleId]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['users', filterRoleId, page, perPage, debouncedSearch, statusFilter],
    queryFn: async () => {
      // Server-side filtering: the backend /user/list supports roleId, search and status.
      const filter: Record<string, string | string[]> = {};
      if (filterRoleId) filter.roleId = filterRoleId;
      if (debouncedSearch) filter.search = debouncedSearch;
      if (statusFilter.length > 0) filter.status = statusFilter;

      const response = await dataProvider.getList('users', {
        pagination: {
          page: page,
          perPage: perPage,
        },
        sort: { field: 'id', order: 'DESC' },
        filter,
      });

      return {
        data: (response?.data || []).map((user: UserRecord) => ({
          ...user,
          name: user.name || '',
          profile: user.profile || '',
          status: (user.status || 'PENDING') as UserStatus,
          role: {
            roleName: user.role?.roleName || '',
          },
          walletAddress: user.walletAddress || '',
        })),
        total: response?.total || 0,
      };
    },
  });

  // Map UserRecord to User type
  const mappedUsers: User[] = useMemo(() => {
    return (data?.data || []).map((user: UserRecord) => ({
      id: user.id || user._id || '',
      _id: user._id,
      name: user.name || null,
      email: user.email || null,
      profile: user.profile || null,
      status: (user.status || 'PENDING') as UserStatus,
      role: {
        roleName: user.role?.roleName || 'USER',
      },
      walletAddress: user.walletAddress || '',
    }));
  }, [data]);

  const total = data?.total || 0;
  const pageCount = Math.ceil(total / perPage);

  // Filtering is handled server-side now; the table renders the current page as-is.
  const filteredUsers = mappedUsers;

  const handleSelectUser = (userId: string, selected: boolean) => {
    if (selected) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedUsers(filteredUsers.map((u) => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) {
      toast({
        description: 'Please select at least one user',
        variant: 'destructive',
      });
      return;
    }

    // Use the existing ApproveViewsButton logic
    // For now, we'll show a toast and refresh
    toast({
      description: `${selectedUsers.length} users will be approved`,
      variant: 'default',
    });

    // The actual approval will be handled by ApproveViewsButton component
    // We'll trigger a refetch after a delay
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      refetch();
      setSelectedUsers([]);
    }, 1000);
  };

  const handleExport = () => {
    // This is handled in UsersFilters component
  };

  const handleAddUser = (formData: UserFormData) => {
    // User is already added via API in AddUserModal
    // Just refresh the list
    queryClient.invalidateQueries({ queryKey: ['users'] });
    refetch();
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    refetch();
  };

  const pendingCount = mappedUsers.filter(
    (u) => u.status === 'PENDING' || u.status === 'pending'
  ).length;

  if (isLoading) {
    return (
      <div className='glass-card rounded-xl flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Users</h1>
          <p className='text-muted-foreground'>
            Manage user registrations, KYC verification, and access control.
            {pendingCount > 0 && (
              <span className='ml-2 text-warning font-medium'>
                {pendingCount} pending approvals
              </span>
            )}
          </p>
        </div>
        {selectedUsers.length > 0 && (
          <ApproveViewsButton
            selectedIds={selectedUsers}
            resource='users'
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['users'] });
              refetch();
              setSelectedUsers([]);
            }}
          />
        )}
      </div>

      {/* Filters */}
      <UsersFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        selectedCount={selectedUsers.length}
        onBulkApprove={handleBulkApprove}
        onExport={handleExport}
        onAddUser={() => setIsAddModalOpen(true)}
        onRefresh={handleRefresh}
      />

      {/* Table */}
      {!Array.isArray(mappedUsers) || mappedUsers.length === 0 ? (
        <div className='glass-card rounded-xl flex items-center justify-center min-h-[400px]'>
          <p className='text-center py-4 text-muted-foreground'>No users found</p>
        </div>
      ) : (
        <UsersTable
          users={filteredUsers}
          selectedUsers={selectedUsers}
          onSelectUser={handleSelectUser}
          onSelectAll={handleSelectAll}
          onRefresh={handleRefresh}
        />
      )}

      {/* Pagination */}
      {/* Pagination */}
      <div className='flex flex-col lg:flex-row items-center justify-between gap-4 py-4 px-4 sm:py-6 sm:px-6 border-t border-border bg-muted/5 rounded-lg'>
        {/* Results Info */}
        <div className='flex flex-wrap items-center gap-2 text-xs sm:text-sm text-muted-foreground'>
          <span>Showing</span>
          <span className='px-2 py-1 rounded-md font-semibold bg-primary/20 text-primary'>
            {(page - 1) * perPage + 1}
          </span>
          <span>to</span>
          <span className='px-2 py-1 rounded-md font-semibold bg-primary/20 text-primary'>
            {Math.min(page * perPage, total)}
          </span>
          <span>of</span>
          <span className='px-2 py-1 rounded-md font-semibold bg-primary/20 text-primary'>
            {total}
          </span>
          <span>users</span>
        </div>

        {/* Pagination Controls */}
        <div className='flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto'>
          {/* Items per page selector */}
          <div className='flex items-center gap-2 w-full sm:w-auto'>
            <label className='text-xs sm:text-sm font-medium text-muted-foreground'>Show:</label>
            <select
              className='h-8 w-16 rounded-md border px-2 text-xs sm:text-sm bg-card border-border text-foreground'
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 30, 50].map((value) => (
                <option key={value} value={value} className='bg-card text-foreground'>
                  {value}
                </option>
              ))}
            </select>
          </div>

          {/* Page navigation */}
          <div className='flex items-center gap-2 w-full sm:w-auto'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            <div className='flex items-center justify-center min-w-[80px] sm:min-w-[100px] px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm font-medium bg-muted text-foreground border border-border'>
              <span className='hidden sm:inline'>
                Page {page} of {pageCount || 1}
              </span>
              <span className='sm:hidden'>
                {page}/{pageCount || 1}
              </span>
            </div>

            <Button
              variant='outline'
              size='sm'
              onClick={() => setPage((p) => (p < pageCount ? p + 1 : p))}
              disabled={page >= pageCount}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      <AddUserModal open={isAddModalOpen} onOpenChange={setIsAddModalOpen} onAdd={handleAddUser} />
    </div>
  );
};

export default Users;
